import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, message, Popconfirm, Space, Table, Tag } from "antd";
import { DeleteOutlined, EditOutlined, FileWordOutlined, HolderOutlined, PlusOutlined, VideoCameraAddOutlined } from "@ant-design/icons";
import { DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import service from "../../api/FetchInterceptor";
import PartForm from "./partForm";
import VideoUpload from "./videoUpload";
import ManualImport from "./manualImport";

const VIDEO_STATUS = {
    none: { color: "default", label: "Pas de vidéo" },
    uploading: { color: "processing", label: "Upload en cours" },
    processing: { color: "gold", label: "Encodage" },
    ready: { color: "green", label: "Vidéo prête" },
    failed: { color: "red", label: "Échec vidéo" },
};

const POLL_INTERVAL = 10000;

const formatDuration = (sec) => {
    if (!sec) return null;
    const m = Math.floor(sec / 60);
    const s = String(sec % 60).padStart(2, "0");
    return `${m}:${s}`;
};

// Ligne de tableau déplaçable (pattern antd + dnd-kit).
const SortableRow = (props) => {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
        id: props["data-row-key"],
    });

    const style = {
        ...props.style,
        transform: CSS.Translate.toString(transform),
        transition,
        ...(isDragging ? { position: "relative", zIndex: 10, background: "#fafafa" } : {}),
    };

    return (
        <tr {...props} ref={setNodeRef} style={style} {...attributes}>
            {Array.isArray(props.children)
                ? props.children.map((child) =>
                      child?.key === "sort" ? (
                          <td key="sort" className="ant-table-cell">
                              <Button
                                  type="text"
                                  size="small"
                                  icon={<HolderOutlined />}
                                  ref={setActivatorNodeRef}
                                  style={{ cursor: "move", touchAction: "none" }}
                                  {...listeners}
                              />
                          </td>
                      ) : (
                          child
                      )
                  )
                : props.children}
        </tr>
    );
};

const FormationParts = ({ formationId, onChange }) => {
    const [parts, setParts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editPart, setEditPart] = useState(null);
    const [videoPart, setVideoPart] = useState(null);
    const [importOpen, setImportOpen] = useState(false);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

    const load = useCallback(() => {
        setLoading(true);
        service({ method: "GET", url: `admin/formations/${formationId}/parts` })
            .then(setParts)
            .finally(() => setLoading(false));
    }, [formationId]);

    useEffect(() => {
        load();
    }, [load]);

    const changed = () => {
        load();
        onChange?.();
    };

    // Pendant l'encodage Bunny, on relit le statut toutes les 10 s (secours si le webhook n'arrive pas).
    const pendingIds = parts
        .filter((p) => p.bunny_video_id && ["processing", "uploading"].includes(p.video_status))
        .map((p) => p.id)
        .join(",");

    useEffect(() => {
        if (!pendingIds) return;

        const timer = setInterval(() => {
            pendingIds.split(",").forEach((id) =>
                service({ method: "GET", url: `admin/parts/${id}/video/status` })
                    .then((updated) => setParts((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p))))
                    .catch(() => {})
            );
        }, POLL_INTERVAL);

        return () => clearInterval(timer);
    }, [pendingIds]);

    const removeVideo = (part) => {
        service({ method: "DELETE", url: `admin/parts/${part.id}/video` })
            .then(() => {
                message.success("Vidéo supprimée");
                load();
            })
            .catch(() => message.error("Erreur lors de la suppression de la vidéo"));
    };

    const onDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;

        const previous = parts;
        const oldIndex = parts.findIndex((p) => p.id === active.id);
        const newIndex = parts.findIndex((p) => p.id === over.id);
        const reordered = arrayMove(parts, oldIndex, newIndex);
        setParts(reordered); // Affichage immédiat, annulé si l'API refuse.

        service({
            method: "POST",
            url: `admin/formations/${formationId}/parts/reorder`,
            data: { ids: reordered.map((p) => p.id) },
        })
            .then(setParts)
            .catch(() => {
                setParts(previous);
                message.error("Impossible d'enregistrer le nouvel ordre");
            });
    };

    const remove = (part) => {
        service({ method: "DELETE", url: `admin/formations/${formationId}/parts/${part.id}` })
            .then(() => {
                message.success("Partie supprimée");
                changed();
            })
            .catch(() => message.error("Erreur lors de la suppression"));
    };

    const columns = useMemo(
        () => [
            { key: "sort", width: 48 },
            {
                key: "position",
                title: "#",
                width: 56,
                render: (_, __, index) => <span className="text-gray-500">{index + 1}</span>,
            },
            {
                key: "title",
                dataIndex: "title",
                title: "Titre",
                render: (title, record) => (
                    <Space>
                        <span className="font-medium">{title}</span>
                        {record.is_free_preview && <Tag color="purple">Aperçu gratuit</Tag>}
                    </Space>
                ),
            },
            {
                key: "content",
                title: "Contenu",
                width: 140,
                render: (_, record) =>
                    record.content ? <Tag color="blue">Rédigé</Tag> : <Tag>Vide</Tag>,
            },
            {
                key: "video_status",
                dataIndex: "video_status",
                title: "Vidéo",
                width: 300,
                render: (status, record) => {
                    const s = VIDEO_STATUS[status] ?? VIDEO_STATUS.none;
                    const duration = status === "ready" ? formatDuration(record.video_duration_sec) : null;

                    return (
                        <Space size={4} wrap>
                            <Tag color={s.color}>
                                {s.label}
                                {duration ? ` · ${duration}` : ""}
                            </Tag>
                            <Button size="small" icon={<VideoCameraAddOutlined />} onClick={() => setVideoPart(record)}>
                                {record.bunny_video_id ? "Remplacer" : "Ajouter"}
                            </Button>
                            {record.bunny_video_id && (
                                <Popconfirm
                                    title="Supprimer la vidéo de cette partie ?"
                                    description="Elle sera aussi supprimée de Bunny."
                                    okText="Supprimer"
                                    cancelText="Annuler"
                                    okButtonProps={{ danger: true }}
                                    onConfirm={() => removeVideo(record)}
                                >
                                    <Button size="small" type="text" danger icon={<DeleteOutlined />} title="Supprimer la vidéo" />
                                </Popconfirm>
                            )}
                        </Space>
                    );
                },
            },
            {
                key: "actions",
                width: 110,
                render: (_, record) => (
                    <Space>
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            title="Modifier"
                            onClick={() => {
                                setEditPart(record);
                                setDrawerOpen(true);
                            }}
                        />
                        <Popconfirm
                            title="Supprimer cette partie ?"
                            okText="Supprimer"
                            cancelText="Annuler"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => remove(record)}
                        >
                            <Button type="text" danger icon={<DeleteOutlined />} title="Supprimer" />
                        </Popconfirm>
                    </Space>
                ),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [formationId]
    );

    return (
        <>
            <VideoUpload
                part={videoPart}
                open={!!videoPart}
                setOpen={(v) => !v && setVideoPart(null)}
                onSuccess={load}
            />

            <ManualImport formationId={formationId} open={importOpen} setOpen={setImportOpen} onSuccess={changed} />

            <PartForm
                formationId={formationId}
                part={editPart}
                open={drawerOpen}
                setOpen={setDrawerOpen}
                onSuccess={changed}
            />

            <div className="mb-4 flex items-center justify-between">
                <p className="m-0 text-sm text-gray-500">
                    Glissez les lignes avec la poignée pour changer l'ordre des parties.
                </p>
                <Space>
                    <Button icon={<FileWordOutlined />} onClick={() => setImportOpen(true)}>
                        Importer un manuel (.docx)
                    </Button>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setEditPart(null);
                            setDrawerOpen(true);
                        }}
                    >
                        Ajouter une partie
                    </Button>
                </Space>
            </div>

            <DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
                <SortableContext items={parts.map((p) => p.id)} strategy={verticalListSortingStrategy}>
                    <Table
                        rowKey="id"
                        loading={loading}
                        columns={columns}
                        dataSource={parts}
                        pagination={false}
                        components={{ body: { row: SortableRow } }}
                        locale={{ emptyText: "Aucune partie pour l'instant" }}
                    />
                </SortableContext>
            </DndContext>
        </>
    );
};

export default FormationParts;
